import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Redirect, useHistory } from "react-router-dom";
import { PageHeaderWithBack } from "../../../../Components/Molecules";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { useToast } from "../../../../Hooks/toast";
import { createBaasCardAccount } from "../../../../Services/baasCardAccountsApi";
import { fetchBaasCardPrograms } from "../../../../Services/baasCardProgramsApi";
import { formatBaasConsoleConflictMessage } from "../../../../Services/baasConsoleClient";
import { formatInputValue } from "../../../../Utils/formatInputValue";
import { parseBrlMajorMask } from "../Instances/baasInstanceCreateConstants";
import { BaasCardsListSkeleton } from "../components/BaasSkeleton";
import {
  BAAS_CARTOES_ACCOUNTS_PATH,
  buildBaasCardAccountDetailPath,
} from "../constants";

const emptyAddress = () => ({
  addressLine1: "",
  addressLine2: "",
  neighborhood: "",
  city: "",
  state: "",
  zipcode: "",
  countryCode: "BR",
});

const emptyForm = () => ({
  cardProgramId: "",
  fullName: "",
  identityDocumentNumber: "",
  phone: "",
  email: "",
  personId: "",
  accountId: "",
  billingAddress: emptyAddress(),
  cardDeliveryAddress: emptyAddress(),
  copyDeliveryFromBilling: true,
  includeCreditInfo: false,
  creditLimitAmount: "",
  withdrawalLimitAmount: "",
  paymentDue: "10",
  creditCurrency: "BRL",
});

const Field = ({ label, children, hint }) => (
  <label className="block min-w-0 space-y-1.5">
    <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">{label}</span>
    {children}
    {hint ? <span className="block text-[11px] text-gray-400 dark:text-gray-500">{hint}</span> : null}
  </label>
);

const inputClass =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-brand-dark outline-none focus:border-gray-400 dark:border-brand-dark-border dark:bg-brand-dark-surface dark:text-gray-100 dark:focus:border-gray-500";

const AddressFields = ({ value, onChange, prefix }) => (
  <div className="grid gap-3 sm:grid-cols-2">
    <Field label={intl.get("baas.cartoes.accounts.create.addressLine1")}>
      <input
        className={inputClass}
        value={value.addressLine1}
        onChange={(e) => onChange({ ...value, addressLine1: e.target.value })}
        autoComplete={`${prefix} address-line1`}
      />
    </Field>
    <Field label={intl.get("baas.cartoes.accounts.create.addressLine2")}>
      <input
        className={inputClass}
        value={value.addressLine2}
        onChange={(e) => onChange({ ...value, addressLine2: e.target.value })}
        autoComplete={`${prefix} address-line2`}
      />
    </Field>
    <Field label={intl.get("baas.cartoes.accounts.create.neighborhood")}>
      <input
        className={inputClass}
        value={value.neighborhood}
        onChange={(e) => onChange({ ...value, neighborhood: e.target.value })}
      />
    </Field>
    <Field label={intl.get("baas.cartoes.accounts.create.city")}>
      <input
        className={inputClass}
        value={value.city}
        onChange={(e) => onChange({ ...value, city: e.target.value })}
        autoComplete={`${prefix} address-level2`}
      />
    </Field>
    <Field label={intl.get("baas.cartoes.accounts.create.state")}>
      <input
        className={inputClass}
        value={value.state}
        onChange={(e) => onChange({ ...value, state: e.target.value })}
        autoComplete={`${prefix} address-level1`}
      />
    </Field>
    <Field label={intl.get("baas.cartoes.accounts.create.zipcode")}>
      <input
        className={inputClass}
        value={value.zipcode}
        onChange={(e) => onChange({ ...value, zipcode: e.target.value })}
        autoComplete={`${prefix} postal-code`}
      />
    </Field>
  </div>
);

export const BaasCardAccountCreatePage = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { selectedInstance } = useBaasInstance();
  const isLoading = useBaasPageLoading(selectedInstance?.id);
  const { can } = useBaasCapabilities();
  const canCreate = can("issuer.programs.create");

  const [programs, setPrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!selectedInstance) {
        setPrograms([]);
        return;
      }

      setProgramsLoading(true);
      try {
        const next = await fetchBaasCardPrograms(selectedInstance);
        if (!cancelled) {
          setPrograms(
            next.filter((program) => String(program.status || "").toUpperCase() === "ACTIVE"),
          );
        }
      } catch (error) {
        if (!cancelled) {
          setPrograms([]);
          addToast({
            type: "error",
            title: formatBaasConsoleConflictMessage(
              error,
              intl.get("baas.cartoes.accounts.create.programsLoadError"),
            ),
          });
        }
      } finally {
        if (!cancelled) setProgramsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [addToast, selectedInstance]);

  const selectedProgram = useMemo(
    () => programs.find((program) => program.id === form.cardProgramId) || null,
    [form.cardProgramId, programs],
  );

  const supportsCreditInfo = Boolean(
    selectedProgram?.raw?.product?.supports_credit_info ||
      String(selectedProgram?.psProductCode || "").startsWith("033101"),
  );

  useEffect(() => {
    setForm((current) => ({
      ...current,
      includeCreditInfo: supportsCreditInfo ? current.includeCreditInfo || supportsCreditInfo : false,
    }));
  }, [supportsCreditInfo]);

  if (!canCreate) {
    return <Redirect to={BAAS_CARTOES_ACCOUNTS_PATH} />;
  }

  if (isLoading || programsLoading) {
    return <BaasCardsListSkeleton count={2} />;
  }

  const update = (patch) => setForm((current) => ({ ...current, ...patch }));

  const handleSubmit = async () => {
    if (!form.cardProgramId) {
      addToast({
        type: "error",
        title: intl.get("baas.cartoes.accounts.create.validation.programRequired"),
      });
      return;
    }

    if (!form.personId?.trim()) {
      if (!form.fullName?.trim() || !form.identityDocumentNumber?.trim()) {
        addToast({
          type: "error",
          title: intl.get("baas.cartoes.accounts.create.validation.ownerRequired"),
        });
        return;
      }
    }

    const billing = form.billingAddress;
    if (!billing.addressLine1?.trim() || !billing.city?.trim() || !billing.state?.trim() || !billing.zipcode?.trim()) {
      addToast({
        type: "error",
        title: intl.get("baas.cartoes.accounts.create.validation.billingRequired"),
      });
      return;
    }

    const delivery = form.copyDeliveryFromBilling
      ? form.billingAddress
      : form.cardDeliveryAddress;

    if (
      !form.copyDeliveryFromBilling &&
      (!delivery.addressLine1?.trim() ||
        !delivery.city?.trim() ||
        !delivery.state?.trim() ||
        !delivery.zipcode?.trim())
    ) {
      addToast({
        type: "error",
        title: intl.get("baas.cartoes.accounts.create.validation.deliveryRequired"),
      });
      return;
    }

    let creditLimitAmount = form.creditLimitAmount;
    let withdrawalLimitAmount = form.withdrawalLimitAmount;
    if (supportsCreditInfo) {
      creditLimitAmount = parseBrlMajorMask(form.creditLimitAmount);
      withdrawalLimitAmount = parseBrlMajorMask(form.withdrawalLimitAmount);
      if (
        !Number.isFinite(creditLimitAmount) ||
        creditLimitAmount < 0 ||
        !Number.isFinite(withdrawalLimitAmount) ||
        withdrawalLimitAmount < 0 ||
        !form.paymentDue
      ) {
        addToast({
          type: "error",
          title: intl.get("baas.cartoes.accounts.create.validation.creditRequired"),
        });
        return;
      }
    }

    setSaving(true);
    try {
      const created = await createBaasCardAccount(selectedInstance, {
        ...form,
        creditLimitAmount,
        withdrawalLimitAmount,
        cardDeliveryAddress: delivery,
        includeCreditInfo: supportsCreditInfo,
      });
      addToast({
        type: "success",
        title: intl.get("baas.cartoes.accounts.create.success"),
      });
      history.push(buildBaasCardAccountDetailPath(created.id));
    } catch (error) {
      addToast({
        type: "error",
        title: formatBaasConsoleConflictMessage(
          error,
          intl.get("baas.cartoes.accounts.create.error"),
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="baas-card-account-create min-w-0 space-y-5">
      <PageHeaderWithBack
        title={intl.get("baas.cartoes.accounts.create.pageTitle")}
        subTitle={intl.get("baas.cartoes.accounts.create.subtitle")}
        backTo={BAAS_CARTOES_ACCOUNTS_PATH}
      />

      <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
        <p className="dashboard-section-label">
          {intl.get("baas.cartoes.accounts.create.sections.program")}
        </p>
        <Field label={intl.get("baas.cartoes.accounts.create.cardProgram")}>
          <select
            className={inputClass}
            value={form.cardProgramId}
            onChange={(e) => update({ cardProgramId: e.target.value })}
          >
            <option value="">
              {intl.get("baas.cartoes.accounts.create.cardProgramPlaceholder")}
            </option>
            {programs.map((program) => (
              <option key={program.id} value={program.id}>
                {program.name} ({program.psProductCode})
              </option>
            ))}
          </select>
        </Field>
        {programs.length === 0 ? (
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {intl.get("baas.cartoes.accounts.create.noActivePrograms")}
          </p>
        ) : null}
      </section>

      <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
        <p className="dashboard-section-label">
          {intl.get("baas.cartoes.accounts.create.sections.owner")}
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label={intl.get("baas.cartoes.accounts.create.personId")}
            hint={intl.get("baas.cartoes.accounts.create.personIdHint")}
          >
            <input
              className={inputClass}
              value={form.personId}
              onChange={(e) => update({ personId: e.target.value })}
              placeholder="pers_…"
            />
          </Field>
          <Field
            label={intl.get("baas.cartoes.accounts.create.accountId")}
            hint={intl.get("baas.cartoes.accounts.create.accountIdHint")}
          >
            <input
              className={inputClass}
              value={form.accountId}
              onChange={(e) => update({ accountId: e.target.value })}
              placeholder="acct_…"
            />
          </Field>
          <Field label={intl.get("baas.cartoes.accounts.create.fullName")}>
            <input
              className={inputClass}
              value={form.fullName}
              onChange={(e) => update({ fullName: e.target.value })}
              disabled={Boolean(form.personId?.trim())}
            />
          </Field>
          <Field label={intl.get("baas.cartoes.accounts.create.document")}>
            <input
              className={inputClass}
              value={form.identityDocumentNumber}
              onChange={(e) => update({ identityDocumentNumber: e.target.value })}
              disabled={Boolean(form.personId?.trim())}
            />
          </Field>
          <Field label={intl.get("baas.cartoes.accounts.create.phone")}>
            <input
              className={inputClass}
              value={form.phone}
              onChange={(e) => update({ phone: e.target.value })}
              disabled={Boolean(form.personId?.trim())}
            />
          </Field>
          <Field label={intl.get("baas.cartoes.accounts.create.email")}>
            <input
              className={inputClass}
              type="email"
              value={form.email}
              onChange={(e) => update({ email: e.target.value })}
              disabled={Boolean(form.personId?.trim())}
            />
          </Field>
        </div>
      </section>

      <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
        <p className="dashboard-section-label">
          {intl.get("baas.cartoes.accounts.create.sections.billing")}
        </p>
        <AddressFields
          prefix="billing"
          value={form.billingAddress}
          onChange={(billingAddress) => update({ billingAddress })}
        />
      </section>

      <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="dashboard-section-label mb-0">
            {intl.get("baas.cartoes.accounts.create.sections.delivery")}
          </p>
          <label className="inline-flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              checked={form.copyDeliveryFromBilling}
              onChange={(e) => update({ copyDeliveryFromBilling: e.target.checked })}
            />
            {intl.get("baas.cartoes.accounts.create.copyDelivery")}
          </label>
        </div>
        {!form.copyDeliveryFromBilling ? (
          <AddressFields
            prefix="shipping"
            value={form.cardDeliveryAddress}
            onChange={(cardDeliveryAddress) => update({ cardDeliveryAddress })}
          />
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.cartoes.accounts.create.copyDeliveryHint")}
          </p>
        )}
      </section>

      {supportsCreditInfo ? (
        <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
          <p className="dashboard-section-label">
            {intl.get("baas.cartoes.accounts.create.sections.credit")}
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label={intl.get("baas.cartoes.accounts.create.creditLimit")}>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                  R$
                </span>
                <input
                  className={`${inputClass} pl-9`}
                  type="text"
                  inputMode="numeric"
                  value={form.creditLimitAmount}
                  onChange={(e) =>
                    update({ creditLimitAmount: formatInputValue(String(e.target.value ?? "")) })
                  }
                />
              </div>
            </Field>
            <Field label={intl.get("baas.cartoes.accounts.create.withdrawalLimit")}>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                  R$
                </span>
                <input
                  className={`${inputClass} pl-9`}
                  type="text"
                  inputMode="numeric"
                  value={form.withdrawalLimitAmount}
                  onChange={(e) =>
                    update({
                      withdrawalLimitAmount: formatInputValue(String(e.target.value ?? "")),
                    })
                  }
                />
              </div>
            </Field>
            <Field label={intl.get("baas.cartoes.accounts.create.paymentDue")}>
              <input
                className={inputClass}
                type="number"
                min="1"
                max="31"
                value={form.paymentDue}
                onChange={(e) => update({ paymentDue: e.target.value })}
              />
            </Field>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.cartoes.accounts.create.creditUnitsHint")}
          </p>
        </section>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 pt-4 dark:border-brand-dark-border">
        <button
          type="button"
          className="btn-outline !text-xs"
          onClick={() => history.push(BAAS_CARTOES_ACCOUNTS_PATH)}
          disabled={saving}
        >
          {intl.get("baas.cartoes.accounts.create.cancel")}
        </button>
        <button
          type="button"
          className="btn-primary !text-xs"
          onClick={handleSubmit}
          disabled={saving || programs.length === 0}
        >
          {saving
            ? intl.get("baas.cartoes.accounts.create.submitting")
            : intl.get("baas.cartoes.accounts.create.submit")}
        </button>
      </div>
    </div>
  );
};
